// Locale display + search backed by the `locale-codes` library (per PR review).
//
// Names come from locale-codes so users see friendly labels ("en - English",
// "en-GB - English (United Kingdom)"). Graph's own curated names take precedence for
// the codes it defines, because some Graph codes are non-standard (e.g. "br" means
// Brazilian in Graph but Breton in ISO-639) or aren't real locales at all
// ("NEUTRAL", "cjk"). Anything the library can't name falls back to the raw code with
// no invented language/country name.

import localeCodes from 'locale-codes';
import { SelectItem } from '@zaiusinc/hera';
import { cgLanguages } from '../types/ContentGraph';
import { canonicalDash, isValidLocale } from './localeValidation';

const SHAPE = /^[a-z]{2}(-[A-Z]{2})?$/;
const MAX_RESULTS = 50;

const curatedNameByValue = new Map<string, string>(cgLanguages.map((l) => [l.value, l.text]));

/**
 * Human-readable label for a locale code in "code - Name" form, e.g. "en - English" or
 * "en-GB - English (United Kingdom)". Graph's curated name wins for known Graph codes; otherwise
 * the locale-codes name (with location when present) is used; otherwise the raw code is returned
 * unchanged.
 */
export function localeLabel(tag: string): string {
  const curated = curatedNameByValue.get(tag);
  if (curated) {
    return `${tag} - ${curated}`;
  }
  const entry = localeCodes.getByTag(tag);
  if (entry && entry.name) {
    const name = entry.location ? `${entry.name} (${entry.location})` : entry.name;
    return `${tag} - ${name}`;
  }
  return tag;
}

// Full searchable catalog: Graph's curated languages first ("main languages on top"),
// then every locale-codes tag matching the accepted two-letter shape, deduped by tag.
// Built once and reused across searches.
let cachedCatalog: SelectItem[] | undefined;

function fullCatalog(): SelectItem[] {
  if (cachedCatalog) {
    return cachedCatalog;
  }
  const items: SelectItem[] = [];
  const seen = new Set<string>();
  for (const language of cgLanguages) {
    items.push({text: localeLabel(language.value), value: language.value});
    seen.add(language.value);
  }
  for (const entry of localeCodes.all) {
    if (entry.tag && !seen.has(entry.tag) && SHAPE.test(entry.tag)) {
      items.push({text: localeLabel(entry.tag), value: entry.tag});
      seen.add(entry.tag);
    }
  }
  cachedCatalog = items;
  return items;
}

/**
 * The curated "main" languages, minus any already excluded (e.g. locales already added
 * to the source). Shown when the search box is empty.
 */
export function mainLanguages(excludeValues: string[]): SelectItem[] {
  const exclude = new Set(excludeValues);
  return cgLanguages
    .filter((language) => !exclude.has(language.value))
    .map((language) => ({text: localeLabel(language.value), value: language.value}));
}

/**
 * String-contains lookup over the catalog (matching the label and the raw code), minus
 * excluded values. A valid locale the library doesn't list is still offered, labelled
 * with its raw code only. Results are capped for a manageable list.
 */
export function searchLocales(query: string, excludeValues: string[]): SelectItem[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return [];
  }
  const exclude = new Set(excludeValues);
  const matches = fullCatalog().filter((item) =>
    !exclude.has(item.value)
    && (item.text.toLowerCase().indexOf(normalized) > -1 || item.value.toLowerCase().indexOf(normalized) > -1));

  const trimmed = query.trim();
  if (isValidLocale(trimmed)) {
    const value = canonicalDash(trimmed);
    if (!exclude.has(value) && !matches.some((item) => item.value === value)) {
      matches.push({text: localeLabel(value), value});
    }
  }
  return matches.slice(0, MAX_RESULTS);
}
